import { PageLocaleEnum } from 'common/enums/PageLocaleEnum'
import React from 'react'
import { thankYouSystemeHostMock } from 'src/public-pages/mocks/thank-you-systeme-host-mock'
import { PublicPageHeading } from 'src/public-pages/shared/components/public-page-heading'
import { PublicPageLink } from 'src/public-pages/shared/components/public-page-link'
import { PublicPageText } from 'src/public-pages/shared/components/public-page-text'
import { useTypedTranslation } from 'src/shared/i18next/hooks/use-typed-translation'
import { CheckCircleIcon } from 'src/shared/icons/check-circle-icon'
import {
  WebsiteThemeInterface,
  WebsiteTypographySettingsStylesInterface,
} from 'src/shared/types/website-settings-types'

export interface ThankYouSystemeHostContentProps {
  heading?: WebsiteTypographySettingsStylesInterface
  paragraph?: WebsiteTypographySettingsStylesInterface
  theme?: WebsiteThemeInterface
  locale?: PageLocaleEnum
}

export const ThankYouSystemeHostContent: React.FC<
  ThankYouSystemeHostContentProps
> = ({ heading, paragraph, theme, locale }) => {
  const { t } = useTypedTranslation()
  const host = `${process.env.CLOUDFRONT_PUBLISHER_ASSETS_HOST}/assets/images/public-pages/`
  const isEnglishLocale = locale === PageLocaleEnum.ENGLISH

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
      }}
    >
      <CheckCircleIcon style={{ marginBottom: '12px' }} />

      <PublicPageHeading
        heading={heading}
        theme={theme}
        style={{ marginBottom: 16, fontWeight: 'bold' }}
      >
        {t('website_public_page.thank_you_content.check_inbox_title')}
      </PublicPageHeading>

      <div style={{ marginBottom: 24, width: '100%' }}>
        <PublicPageText
          paragraph={paragraph}
          theme={theme}
          textAlign="center"
          style={{ width: '100%' }}
        >
          <p style={{ margin: '0 0 0.25em' }}>
            {t(
              'website_public_page.thank_you_content.confirmation_email_prefix',
            )}{' '}
            <strong>
              {t(
                'website_public_page.thank_you_content.confirmation_email_strong',
              )}
            </strong>
          </p>
          <p>
            <strong>
              {t('website_public_page.thank_you_content.click_strong')}
            </strong>{' '}
            {t('website_public_page.thank_you_content.click_suffix')}
          </p>
        </PublicPageText>
      </div>

      {isEnglishLocale && (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            marginBottom: 24,
            gap: 24,
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 12,
            }}
          >
            <a
              href={thankYouSystemeHostMock.gmail.href}
              target="_blank"
              rel="noopener noreferrer"
              style={{ lineHeight: 0 }}
            >
              <img
                src={`${host}${thankYouSystemeHostMock.gmail.logoAsset}`}
                alt="Gmail"
                width={60}
                height={45}
              />
            </a>
            <PublicPageLink
              href={thankYouSystemeHostMock.gmail.href}
              target="_blank"
              rel="noopener noreferrer"
              paragraph={paragraph}
              theme={theme}
              style={{
                textDecoration: 'underline',
              }}
            >
              {t('website_public_page.thank_you_content.open_gmail')}
            </PublicPageLink>
          </div>

          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 12,
            }}
          >
            <a
              href={thankYouSystemeHostMock.outlook.href}
              target="_blank"
              rel="noopener noreferrer"
              style={{ lineHeight: 0 }}
            >
              <img
                src={`${host}${thankYouSystemeHostMock.outlook.logoAsset}`}
                alt="Outlook"
                width={60}
                height={55}
              />
            </a>
            <PublicPageLink
              href={thankYouSystemeHostMock.outlook.href}
              target="_blank"
              rel="noopener noreferrer"
              paragraph={paragraph}
              theme={theme}
              style={{
                textDecoration: 'underline',
              }}
            >
              {t('website_public_page.thank_you_content.open_outlook')}
            </PublicPageLink>
          </div>
        </div>
      )}

      <PublicPageText
        paragraph={paragraph}
        theme={theme}
        textAlign="center"
        style={{ marginBottom: 24 }}
      >
        <p style={{ margin: '0 0 0.25em' }}>
          {t('website_public_page.thank_you_content.spam_line1')}
        </p>
        <p>
          {t('website_public_page.thank_you_content.spam_prefix')}{' '}
          <strong>
            {t('website_public_page.thank_you_content.spam_strong')}
          </strong>
        </p>
      </PublicPageText>

      <PublicPageText paragraph={paragraph} theme={theme} textAlign="center">
        <p style={{ margin: '0 0 0.25em' }}>
          {t('website_public_page.thank_you_content.see_you_soon')}
        </p>
        <p>
          {t('website_public_page.thank_you_content.systeme_team_signature')}
        </p>
      </PublicPageText>
    </div>
  )
}
